import React, { useContext, useEffect, useState } from "react";
import "./UsageDataTable.scss";
import { ReactComponent as ProfileIcon } from "../../assets/images/insight-profile.svg";
import { ReactComponent as ArrowDown } from "../../assets/images/arrow-down-usage.svg";
import { ReactComponent as ArrowUp } from "../../assets/images/arrow-up-usage.svg";
import { useTranslation } from "react-i18next";
import AppContext from "../../config/AppContext";
import InfiniteScroll from "react-infinite-scroll-component";
import { Avatar, Tooltip, CircularProgress, Box } from "@mui/material";
import { getMemberRankList } from "../../services/UsageInsightService";
import { ReactComponent as EmptyMemberList } from "../../assets/images/ralvie/empty-member-list.svg";
import { ReactComponent as EmptyMemberListDark } from "../../assets/images/ralvie/empty-member-list-dark.svg";
import {ReactComponent as Info} from "../../assets/images/ralvie/worklog-info.svg"
import {ReactComponent as InfoDark} from "../../assets/images/ralvie/info-dark.svg"

const UsageDataTable = ({ selectedDate, departmentIds, refresh, setRefresh }) => {
  const { t } = useTranslation();
  const { mode } = useContext(AppContext);
  const [sortConfig, setSortConfig] = useState('asc');
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const requestSort = (e) => {
    e.stopPropagation();
    setPageNumber(1);
    setSortConfig(prev => prev === 'desc' ? 'asc' : 'desc');
    fetchData(1, pageSize, sortConfig === 'asc' ? 'asc' : 'desc');
  };

  const sortedData = (data, tlCnt, sorting) => {
    if (!data || data.length === 0) return [];
    const sorted = data.sort((a, b) => {
      if (a.totalScore === b.totalScore) {
        return sorting === 'desc'
          ? b.name.localeCompare(a.name)
          : a.name.localeCompare(b.name);
      }
      if (sorting === 'desc') {
        return a.totalScore - b.totalScore;
      } else {
        return b.totalScore - a.totalScore;
      }
    });
    return sorted.map((item, idx) => {
      return {
        ...item,
        rank: sorting === 'asc'
          ? (idx + 1)
          : (tlCnt - idx),
      }
    });
  };

  const columns = [
    {
      field: "name",
      headerName: t("RAL0239"),
      flex: 1,
      renderCell: (params) => (
        <div className="usage-member-cell">
          {params.row?.profilePhoto ?
            <Avatar src={params.row?.profilePhoto} alt="Member photo" sx={{ height: "32px", width: "32px" }} />
            : <ProfileIcon />}
          <Tooltip title={`${params.value}`} placement="bottom-start">
            <span>{params.value}</span>
          </Tooltip>
        </div>
      )
    },
    {
      field: "department",
      headerName: t("RAL0245"),
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={`${params.row?.department?.name ? params.row?.department?.name : ""}`} placement="bottom-start">
          <span>{params.row?.department?.name ? params.row?.department?.name : "-"}</span>
        </Tooltip>
      )
    },
    {
      field: "designation",
      headerName: t("RAL0244"),
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={`${params.row?.designation?.name ? params.row?.designation?.name : ""}`} placement="bottom-start">
          <span>{params.row?.designation?.name ? params.row?.designation?.name : "-"}</span>
        </Tooltip>
      )
    },
    {
      field: "totalScore",
          headerName: (
      <div  className="infodark-alignment">
        {t("RAL1225")}
        <Tooltip title={t("RAL1235")} placement="top">
          {mode==="dark"?<InfoDark/>:<Info />}
        </Tooltip>
      </div>
    ),
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={`${params.value}`} placement="bottom-start">
          <span>{params.value}</span>
        </Tooltip>
      )
    },

    {
      field: "rank",
      headerName: t("RAL1224"),
      sortable: true,
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={`${params.value}`} placement="bottom-start">
          <span>{params.value}</span>
        </Tooltip>
      )
    }
  ];

  const fetchData = (pgNm = 1, pgSz = 20, sorting = "desc") => {
    if (pageNumber * pageSize > 100) {
      setHasMore(false);
      return;
    }
    if (pgNm === 1) {
      setData([]);
      setTotalCount(0);
      setIsLoading(true);
      setHasMore(true);
    }

    let startDate = selectedDate.startOf("day");
    getMemberRankList({
      startDate: startDate.isUTC()
        ? startDate.format()
        : startDate.utc().format(),
      pageNumber: pgNm,
      pageSize: pgSz,
      departmentIds: departmentIds == "All departments" ? "" : departmentIds,
      sortOrder: sorting,
    })
      .then(response => {
        if (response.status === 200 && response.data?.code === "RCI0000") {
          const tlCnt = response.data?.data?.totalCount || 0;
          const sort = sorting === 'desc' ? 'asc' : 'desc'
          if (pgNm === 1) {
            setData(sortedData(response.data?.data?.users || [], tlCnt, sort));
          }
          else
            setData(prev => sortedData([...prev, ...response.data?.data?.users || []], tlCnt, sort));
          setTotalCount(tlCnt);
        } else {
          setData([]);
        }
      })
      .catch(error => {
        console.error("Error fetching member rank data:", error);
      })
      .finally(() => {
        setIsLoading(false);
        setRefresh(false);
      });
  }

  useEffect(() => {
    if (refresh) {
      setPageNumber(1);
      setHasMore(true);
      setRefresh(false);
      setSortConfig('asc');
    }
  }, [refresh]);

  useEffect(() => {
    fetchData(1, pageSize, 'desc');
    setPageNumber(1);
    setSortConfig('asc');
  }, [selectedDate, departmentIds ]);

  const fetchMoreData = () => {
    const nextPage = pageNumber + 1;
    const maxLimit = 100;
    if (nextPage * pageSize > maxLimit || data.length >= totalCount) {
      setHasMore(false);
      return;
    }
    fetchData(nextPage, pageSize,sortConfig === 'asc' ? 'desc' : 'asc');
    setPageNumber(nextPage);
  };

  if (columns) {
    return (
      <div className="usage-data-table">
        <div className={mode === "dark" ? "usage-table-header-dark" : "usage-table-header"}>
          {columns.map((column, index) => (
            <div
              key={column.field || index}
              style={{ flex: column.flex || 1 }}
              className={column.sortable ? 'sortable-header' : ''}
            >
              {column.headerName}
              {column.sortable && (
                <span className="sort-icon" onClick={(e) => column.sortable && requestSort(e)}>
                  <ArrowUp
                    className={`${mode === "dark" ? "arrow-icon-dark" : "arrow-icon-light"} ${sortConfig === 'asc' ? 'active' : 'inactive'}`}
                  />
                  <ArrowDown
                    className={`${mode === "dark" ? "arrow-icon-dark" : "arrow-icon-light"} ${sortConfig === 'desc' ? 'active' : 'inactive'}`}
                  />
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="usage-table-body" id="scrollableDiv" >
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                paddingTop: 16,
              }}
            >
              <CircularProgress id="project-circular-icon" />
            </Box>
          ) : (totalCount === 0 || data?.length === 0) ? (
            <Box className="empty-state-container">
              {mode == "light" ? (
                <EmptyMemberList id="member-empty-list-icon" />
              ) : (
                <EmptyMemberListDark id="member-empty-list-icon" />
              )}
              <span className={mode === "dark" ? "skip-text-dark" : "skip-text"} style={{ marginTop: "10px" }}>
                {t("RAL0385")}
              </span>
            </Box>
          ) : (
            <InfiniteScroll
              dataLength={data?.length? data?.length : 20}
              next={fetchMoreData}
              hasMore={hasMore}
              scrollableTarget={"scrollableDiv"}>
              {data?.map((row, rowIndex) => (
                <div className={mode === "dark" ? "usage-table-row-dark" : "usage-table-row"} key={rowIndex}>
                  {columns.map((column, colIndex) => {
                    const cellValue = row[column.field];
                    const cellContent = column.renderCell
                      ? column.renderCell({ row, value: cellValue })
                      : cellValue;

                    return (
                      <div
                        key={column.field || colIndex}
                        className={column.field === "name" ? "usage-member-cell" : ""}
                        title={column.field !== "name" && typeof cellValue === 'string' ? cellValue : ''}
                      >
                        {column.field === "name" && !column.renderCell ? (
                          <>
                            <ProfileIcon />
                            <span>{cellContent}</span>
                          </>
                        ) : (
                          cellContent
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </InfiniteScroll>)}
        </div>
      </div>
    );
  }
};

export default UsageDataTable;